import React, { useEffect, useState } from 'react';
import {
  Grid,
  TextField,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Tooltip
} from '@material-ui/core';
import { ListItemText } from '@material-ui/core';
import SubdirectoryArrowRightIcon from '@material-ui/icons/SubdirectoryArrowRight';
import { makeStyles } from '@material-ui/styles';
import { constants } from 'views/Campaign';
import { getAllLists, getAllSegmentedLists } from 'store/lists/actions';
import { useDispatch, useSelector } from 'react-redux';
import { Autocomplete } from '@material-ui/lab';
import { Info } from '@material-ui/icons';
import { SkeletonCardFields } from 'components/Loading';
import { ProductType } from 'constants.js';
import { getEnvVar } from 'helpers';
import { SelectInput } from 'components';
import { getEventTypeOptions } from 'helpers/templateHelper';

const EventDriven = props => {
  const { campaign, disabled } = props;
  const {
    thisCampaign,
    handleProductTypeChange,
    handleEventTypeChange,
    onBlur,
    onFocus,
    shouldShowError,
    getErrorMessage,
    supportedScheduleTypeOptions,
    supportedOnDemandTypeOptions,
    supportedEventTypeOptions,
    supportedInternalSurveyResponseTypeOptions,
    supportedExternalSurveyResponseTypeOptions,
    productIntegrationOptions,
    handleTypeChange,
    handleSubscriberListChange
  } = campaign;

  const {
    ON_EVENT,
    INTERNAL_SURVEY_RESPONSE,
    EXTERNAL_SURVEY_RESPONSE,
    SCHEDULED,
    ON_DEMAND
  } = constants.campaignOccurrenceTypes;

  const dispatch = useDispatch();

  const allLists = useSelector(store => store.lists.allLists.current);
  const allSegmentedLists = useSelector(
    store => store.lists.allSegmentedLists.current
  );
  const gettingAllLists = useSelector(store => store.lists.allLists.getting);
  const gettingAllSegmentedLists = useSelector(
    store => store.lists.allSegmentedLists.getting
  );

  const [subscribersList, setSubscribersList] = useState([]);

  useEffect(() => {
    if (
      thisCampaign.productType === ProductType.List &&
      allLists.length === 0 &&
      allSegmentedLists.length === 0
    ) {
      dispatch(getAllLists());
      dispatch(getAllSegmentedLists());
    }
  }, [thisCampaign.productType]);

  useEffect(() => {
    let options = [];
    function handleScheduled() {
      options = supportedScheduleTypeOptions?.filter(
        option => option.type === 'EVENT_TYPE_CATEGORY'
      );
      if (options.length === 1) {
        handleProductTypeChange({
          target: {
            name: 'schemaIdentifier',
            value: options[0]?.schemaIdentifier
          }
        });
      }
    }
    function handleOndemand() {
      options = supportedOnDemandTypeOptions?.filter(
        option => option.type === 'EVENT_TYPE_CATEGORY'
      );
      if (options.length === 1) {
        handleProductTypeChange({
          target: {
            name: 'schemaIdentifier',
            value: options[0]?.schemaIdentifier
          }
        });
      }
    }
    function handleOnevent() {
      options = supportedEventTypeOptions?.filter(
        option => option.type === 'EVENT_TYPE'
      );
      if (options.length === 1) {
        handleEventTypeChange({
          target: {
            name: 'schemaIdentifier',
            value: options[0]?.schemaIdentifier
          }
        });
      }
    }
    function handleInternalSurvey() {
      options = supportedInternalSurveyResponseTypeOptions?.filter(
        option => option.type === 'EVENT_TYPE_CATEGORY'
      );
      if (options.length === 1) {
        handleEventTypeChange({
          target: {
            name: 'schemaIdentifier',
            value: options[0]?.schemaIdentifier
          }
        });
      }
    }
    function handleExternalSurvey() {
      options = supportedExternalSurveyResponseTypeOptions?.filter(
        option => option.type === 'EVENT_TYPE_CATEGORY'
      );
      if (options.length === 1) {
        handleEventTypeChange({
          target: {
            name: 'schemaIdentifier',
            value: options[0]?.schemaIdentifier
          }
        });
      }
    }
    if (thisCampaign.productType !== ProductType.List) {
      switch (thisCampaign.campaignOccurrenceType) {
        case SCHEDULED:
          handleScheduled(); break;
        case ON_DEMAND:
          handleOndemand(); break;
        case ON_EVENT:
          handleOnevent(); break;
        case INTERNAL_SURVEY_RESPONSE:
          handleInternalSurvey(); break;
        case EXTERNAL_SURVEY_RESPONSE:
          handleExternalSurvey(); break;
        default:
          break;
      }
    }
  }, [thisCampaign.campaignOccurrenceType, thisCampaign.productType]);

  useEffect(() => {
    if (
      !gettingAllLists &&
      !gettingAllSegmentedLists &&
      (allSegmentedLists.length > 0 || allLists.length > 0)
    ) {
      let listsMap = {};
      allLists.map(list => {
        listsMap[list.id] = {
          ...list,
          searchText: list.name.toLowerCase(),
          children: []
        };
      });
      allSegmentedLists.map(segList => {
        let parentList = listsMap[segList.listId];
        parentList.searchText = `${
          parentList.searchText
          }.${segList.name.toLowerCase()}`;
        parentList.children = [
          ...parentList.children,
          { ...segList, searchText: segList.name.toLowerCase() }
        ];
      });
      let finalList = [];
      Object.values(listsMap).forEach(list => {
        finalList.push({ ...list });
        if (list.children.length > 0) {
          const sortedList = [...list.children].sort((list1, list2) =>
            list1.name < list2.name ? -1 : 1
          );
          finalList.push(...sortedList);
        }
      });
      setSubscribersList(finalList);
    }
  }, [allSegmentedLists, allLists]);

  const level1 = makeStyles(theme => ({
    root: {
      paddingLeft: theme.spacing(1),
      pointerEvents: 'none',
      backgroundColor: '#A5A5A5',
      '&:hover': {
        backgroundColor: '#A5A5A5'
      }
    }
  }))();

  const level2 = makeStyles(theme => ({
    root: {
      paddingLeft: theme.spacing(3),
      pointerEvents: 'none',
      backgroundColor: '#CECECE',
      '&:hover': {
        backgroundColor: '#CECECE'
      }
    }
  }))();

  const level3 = makeStyles(theme => ({
    root: {
      paddingLeft: theme.spacing(5),
      pointerEvents: 'none',
      backgroundColor: '#E5E5E5',
      '&:hover': {
        backgroundColor: '#E5E5E5'
      }
    }
  }))();

  const level4 = makeStyles(theme => ({
    root: {
      paddingLeft: theme.spacing(7),
      backgroundColor: '#FFFFFF'
    }
  }))();

  const iconStyle = makeStyles(() => ({
    root: {
      minWidth: '30px'
    }
  }))();

  const segListStyle = makeStyles(theme => ({
    root: {
      marginLeft: theme.spacing(1),
      color: 'grey'
    }
  }))();

  const filterSubscribersList = (options, state) => {
    let searchInput = state.inputValue.toLowerCase();
    return options.filter(option => option.searchText.includes(searchInput));
  };

  const getSubscribersListValue = () => {
    let selectedList = subscribersList.filter(list =>
      thisCampaign.segmentedListId && thisCampaign.segmentedListId !== ''
        ? list.id === thisCampaign.segmentedListId
        : list.id === thisCampaign.subscriberListId
    );
    return selectedList.length === 1 ? selectedList[0] : '';
  };

  return (
    <Grid container spacing={12}>
      {getEnvVar('ADM_INCLUDE_LIST_MANAGEMENT') === 'true' &&
        (thisCampaign.campaignOccurrenceType === 'ON_DEMAND' ||
          ((thisCampaign.campaignOccurrenceType === 'INTERNAL_SURVEY_RESPONSE' || 
            thisCampaign.campaignOccurrenceType === 'EXTERNAL_SURVEY_RESPONSE') &&
            getEnvVar('ADM_INCLUDE_LIST_MANAGEMENT_SURVEYS') === 'true')) && (
          <Grid item xs={12}>
            <FormControl component="fieldset">
              <FormControlLabel
                label="Type:"
                labelPlacement="start"
                control={
                  <RadioGroup
                    style={{ paddingTop: '8px', marginLeft: '8px' }}
                    aria-label="onDemand type"
                    name="productType"
                    value={thisCampaign.productType || ProductType.Product}
                    onChange={handleTypeChange}
                    row>
                    <FormControlLabel
                      value={ProductType.Product}
                      label="Product type"
                      disabled={disabled}
                      labelPlacement="end"
                      control={<Radio color="primary" />}
                    />
                    <FormControlLabel
                      value={ProductType.List}
                      label="Subscribers list"
                      disabled={disabled}
                      labelPlacement="end"
                      control={<Radio color="primary" />}
                    />
                    {thisCampaign.campaignOccurrenceType !==
                      'INTERNAL_SURVEY_RESPONSE' &&
                      thisCampaign.campaignOccurrenceType !==
                      'EXTERNAL_SURVEY_RESPONSE' && (
                        <FormControlLabel
                          value={ProductType.GUEST_INSIGHTS}
                          label="Guest Insights"
                          disabled={disabled}
                          labelPlacement="end"
                          control={<Radio color="primary" />}
                        />
                      )}
                  </RadioGroup>
                }
              />
            </FormControl>
            <Tooltip
              title={`Choose 'Product Type' for selecting events from the enabled products (or) 
            Choose 'Subscriber List' for selecting list${((getEnvVar(
              'ADM_INCLUDE_LIST_MANAGEMENT_PHASE2' === 'true') && getEnvVar(
                'ADM_INCLUDE_LIST_MANAGEMENT_SEGMENT_LIST' === 'true')) &&
                '/segmented list'
              ) || ''} of list management${(thisCampaign.campaignOccurrenceType !==
                'INTERNAL_SURVEY_RESPONSE' &&
                thisCampaign.campaignOccurrenceType !==
                'EXTERNAL_SURVEY_RESPONSE') && ` (or)
              Choose 'Guest Insights' to target guest profiles` || ''}`}>
              <Info
                size="small"
                style={{ marginTop: '16px', marginLeft: '12px' }}
              />
            </Tooltip>
          </Grid>
        )}

      {getEnvVar('ADM_INCLUDE_LIST_MANAGEMENT') !== 'true' &&
        (thisCampaign.campaignOccurrenceType === 'ON_DEMAND') && (
          <Grid item xs={12}>
            <FormControl component="fieldset">
              <FormControlLabel
                label="Type:"
                labelPlacement="start"
                control={
                  <RadioGroup
                    style={{ paddingTop: '8px', marginLeft: '8px' }}
                    aria-label="onDemand type"
                    name="productType"
                    value={thisCampaign.productType || ProductType.Product}
                    onChange={handleTypeChange}
                    row>
                    <FormControlLabel
                      value={ProductType.Product}
                      label="Product type"
                      disabled={disabled}
                      labelPlacement="end"
                      control={<Radio color="primary" />}
                    />
                    <FormControlLabel
                      value={ProductType.GUEST_INSIGHTS}
                      label="Guest Insights"
                      disabled={disabled}
                      labelPlacement="end"
                      control={<Radio color="primary" />}
                    />
                  </RadioGroup>
                }
              />
            </FormControl>
            <Tooltip
              title={`Choose 'Product Type' for selecting events from the enabled products (or) 
              Choose 'Guest Insights' to target guest profiles`}>
              <Info
                size="small"
                style={{ marginTop: '16px', marginLeft: '12px' }}
              />
            </Tooltip>
          </Grid>
        )}

      <Grid item md={6} xs={12}>
        <Grid item xs={12}>
          {thisCampaign.campaignOccurrenceType === ON_EVENT && (
            <SelectInput
              fullWidth
              label="Send Campaign When"
              margin="dense"
              name="schemaIdentifier"
              onChange={handleEventTypeChange}
              onFocus={onFocus}
              onBlur={onBlur}
              disabled={disabled}
              required
              value={thisCampaign.schemaIdentifier || ''}
              selectProps={{
                renderValue: option => option
              }}
              error={shouldShowError('schemaIdentifier')}
              helperText={getErrorMessage('schemaIdentifier')}
              options={getEventTypeOptions(supportedEventTypeOptions, true)}
            />
          )}
          {(thisCampaign.campaignOccurrenceType === INTERNAL_SURVEY_RESPONSE ||
            thisCampaign.campaignOccurrenceType === EXTERNAL_SURVEY_RESPONSE) &&
            thisCampaign.productType !== ProductType.List && 
            thisCampaign.productType !== ProductType.GUEST_INSIGHTS && (
              <SelectInput
                fullWidth
                label="Send Campaign When"
                margin="dense"
                name="schemaIdentifier"
                onChange={handleEventTypeChange}
                onFocus={onFocus}
                onBlur={onBlur}
                disabled={disabled}
                required
                value={thisCampaign.schemaIdentifier || ''}
                selectProps={{
                  renderValue: option => option
                }}
                error={shouldShowError('schemaIdentifier')}
                helperText={getErrorMessage('schemaIdentifier')}
                options={getEventTypeOptions(
                  supportedInternalSurveyResponseTypeOptions
                )}
              />
            )}
          {getEnvVar('ADM_INCLUDE_LIST_MANAGEMENT') === 'true' &&
            (thisCampaign.campaignOccurrenceType === ON_DEMAND ||
              ((thisCampaign.campaignOccurrenceType === INTERNAL_SURVEY_RESPONSE ||
                thisCampaign.campaignOccurrenceType ===EXTERNAL_SURVEY_RESPONSE) &&
                getEnvVar('ADM_INCLUDE_LIST_MANAGEMENT_SURVEYS') === 'true')) &&
            thisCampaign.productType === ProductType.List && (
              <React.Fragment>
                {gettingAllLists || gettingAllSegmentedLists ? (
                  <SkeletonCardFields numberOfRows={1} />
                ) : (
                  <Autocomplete
                    disabled={
                      disabled &&
                      (!thisCampaign?._isCopy || !!(thisCampaign?.uuid || thisCampaign?.id))
                    }
                    id="size-small-outlined"
                    size="small"
                    name="subscribersList"
                    value={getSubscribersListValue()}
                    onChange={handleSubscriberListChange}
                    options={subscribersList || []}
                    getOptionLabel={option => option.name}
                    filterOptions={(options, state) =>
                      filterSubscribersList(options, state)
                    }
                    renderOption={option => (
                      <React.Fragment>
                        {option.listId ? (
                          <React.Fragment>
                            <SubdirectoryArrowRightIcon
                              classes={segListStyle}
                            />
                            <ListItemText>{option.name}</ListItemText>
                          </React.Fragment>
                        ) : (
                          <ListItemText>{option.name}</ListItemText>
                        )}
                      </React.Fragment>
                    )}
                    renderInput={params => (
                      <TextField
                        {...params}
                        margin="dense"
                        label="Select a subscriber list"
                        variant="outlined"
                        error={shouldShowError('subscriberListId')}
                        helperText={getErrorMessage('subscriberListId')}
                      />
                    )}
                  />
                )}
              </React.Fragment>
            )}
          {(thisCampaign.campaignOccurrenceType === SCHEDULED ||
            (thisCampaign.campaignOccurrenceType === ON_DEMAND &&
              thisCampaign.productType !== ProductType.List && 
              thisCampaign.productType !== ProductType.GUEST_INSIGHTS)) && (
              <SelectInput
                fullWidth
                label="Product type"
                margin="dense"
                name="schemaIdentifier"
                onChange={handleProductTypeChange}
                onFocus={onFocus}
                onBlur={onBlur}
                disabled={disabled}
                value={thisCampaign.schemaIdentifier || ''}
                selectProps={{
                  renderValue: option => option
                }}
                error={shouldShowError('productName')}
                helperText={getErrorMessage('productName')}
                options={getEventTypeOptions(productIntegrationOptions)}
              />
            )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default EventDriven;
